import useLayoutValuesRefresh from './useLayoutValuesRefresh';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import AuthorizationError from '../../components/2compounds/AuthorizationError';
import { Loader } from '../../components/1elements';
import {
  getDashboardErrorMessage,
  useGetDashboardSeriesQuery,
  useGetDashboardWidgetsQuery,
  useGetEffectiveDashboardQuery
} from './dashboardApi';
import { DASHBOARD_SOURCE } from './dashboardContract';
import DashboardArchivedMetricHistory from './DashboardArchivedMetricHistory';
import DashboardFeedback from './DashboardFeedback';
import DashboardVisualizationChart from './DashboardVisualizationChart';
import DashboardWidgetCard from './DashboardWidgetCard';
import { canViewDashboardData } from './dashboardPermissions';
import {
  DASHBOARD_DATE_RANGE,
  DASHBOARD_DATE_RANGE_OPTIONS,
  resolveDashboardDateRange
} from './dashboardViewModel';
import { sortCalculatedDashboardWidgets } from './dashboardWidgetViewModel';

const DASHBOARD_VIEW_TAB = Object.freeze({
  DASHBOARD: 'dashboard',
  FEEDBACK: 'feedback'
});

const DashboardViewerShell = ({ children, locationId }) => {
  const [activeTab, setActiveTab] = useState(DASHBOARD_VIEW_TAB.DASHBOARD);

  return (
    <Box
      data-testid="dynamic-dashboard-viewer"
      sx={{
        height: '100%',
        overflowY: 'auto',
        px: { xs: 2, md: 3 },
        pb: { xs: 2, md: 3 },
        width: '100%'
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
          pt: { xs: 1, md: 1.5 }
        }}
      >
        <Tabs
          aria-label="Dashboard sections"
          onChange={(_event, value) => setActiveTab(value)}
          value={activeTab}
        >
          <Tab
            aria-controls="dashboard-view-panel"
            id="dashboard-view-tab"
            label="Dashboard"
            value={DASHBOARD_VIEW_TAB.DASHBOARD}
          />
          <Tab
            aria-controls="dashboard-feedback-panel"
            id="dashboard-feedback-tab"
            label="Feedback"
            value={DASHBOARD_VIEW_TAB.FEEDBACK}
          />
        </Tabs>
      </Box>

      <Box
        aria-labelledby="dashboard-view-tab"
        data-testid="dashboard-view-panel"
        hidden={activeTab !== DASHBOARD_VIEW_TAB.DASHBOARD}
        id="dashboard-view-panel"
        role="tabpanel"
      >
        {children}
      </Box>
      <Box
        aria-labelledby="dashboard-feedback-tab"
        data-testid="dashboard-feedback-panel"
        hidden={activeTab !== DASHBOARD_VIEW_TAB.FEEDBACK}
        id="dashboard-feedback-panel"
        role="tabpanel"
      >
        <DashboardFeedback locationId={locationId} />
      </Box>
    </Box>
  );
};

const DashboardViewer = ({ locationId }) => {
  const canView = canViewDashboardData();
  const today = dayjs().format('YYYY-MM-DD');
  const defaultCustomRange = resolveDashboardDateRange(
    DASHBOARD_DATE_RANGE.LAST_30_DAYS,
    null,
    null
  );
  const [rangeSelection, setRangeSelection] = useState(
    DASHBOARD_DATE_RANGE.LAST_12_MONTHS
  );
  const [customFromDate, setCustomFromDate] = useState(
    defaultCustomRange.fromDate
  );
  const [customToDate, setCustomToDate] = useState(defaultCustomRange.toDate);
  const dateRange = useMemo(
    () =>
      resolveDashboardDateRange(rangeSelection, customFromDate, customToDate),
    [customFromDate, customToDate, rangeSelection]
  );

  const effectiveQuery = useGetEffectiveDashboardQuery(locationId, {
    refetchOnMountOrArgChange: true,
    skip: !locationId || !canView
  });
  const widgetsQuery = useGetDashboardWidgetsQuery(locationId, {
    refetchOnMountOrArgChange: true,
    skip: !locationId || !canView
  });
  const effectiveDashboard = effectiveQuery.data;
  const shouldLoadSeries =
    Boolean(locationId && canView && dateRange && effectiveDashboard) &&
    (effectiveDashboard.visualizations?.length ?? 0) > 0;
  const seriesQuery = useGetDashboardSeriesQuery(
    {
      locationId,
      fromDate: dateRange?.fromDate,
      toDate: dateRange?.toDate
    },
    { skip: !shouldLoadSeries, refetchOnMountOrArgChange: true }
  );

  useLayoutValuesRefresh(locationId, () => {
    if (locationId && canView) widgetsQuery.refetch();
    if (shouldLoadSeries) seriesQuery.refetch();
  });

  if (!canView) return <AuthorizationError />;
  if (!locationId) {
    return (
      <Alert severity="error">
        A location is required to view a dashboard.
      </Alert>
    );
  }
  if (effectiveQuery.isLoading) {
    return (
      <DashboardViewerShell locationId={locationId}>
        <Loader />
      </DashboardViewerShell>
    );
  }

  const initialError = effectiveQuery.error;
  if (initialError) {
    return (
      <DashboardViewerShell locationId={locationId}>
        <Alert severity="error">{getDashboardErrorMessage(initialError)}</Alert>
      </DashboardViewerShell>
    );
  }

  if (!effectiveDashboard) {
    return (
      <DashboardViewerShell locationId={locationId}>
        <Alert severity="info">
          No effective dashboard is available. Feedback remains available from
          the Feedback tab.
        </Alert>
      </DashboardViewerShell>
    );
  }

  const widgets = sortCalculatedDashboardWidgets(
    widgetsQuery.data?.widgets ?? []
  );
  const visualizations = [...(effectiveDashboard.visualizations ?? [])].sort(
    (left, right) => left.displayOrder - right.displayOrder
  );

  return (
    <DashboardViewerShell locationId={locationId}>
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box>
          <Typography
            component="h1"
            fontSize={{ xs: 24, md: 30 }}
            fontWeight={700}
          >
            {effectiveDashboard.name}
          </Typography>
          {effectiveDashboard.description && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {effectiveDashboard.description}
            </Typography>
          )}
        </Box>
        <Chip
          color={
            effectiveDashboard.source === DASHBOARD_SOURCE.CUSTOMER
              ? 'primary'
              : 'default'
          }
          label={
            effectiveDashboard.source === DASHBOARD_SOURCE.CUSTOMER
              ? 'Customer dashboard'
              : 'Default dashboard'
          }
          size="small"
          variant="outlined"
        />
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Typography
          component="h2"
          fontSize={18}
          fontWeight={700}
          sx={{ mb: 1.5 }}
        >
          Dashboard highlights
        </Typography>
        {widgetsQuery.isLoading ? (
          <Paper
            elevation={0}
            role="status"
            sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}
          >
            <Typography color="text.secondary">
              Loading dashboard widgets…
            </Typography>
            <LinearProgress sx={{ mt: 1.5 }} />
          </Paper>
        ) : widgetsQuery.error ? (
          <Alert severity="warning">
            {getDashboardErrorMessage(
              widgetsQuery.error,
              'Dashboard widgets could not be loaded.'
            )}
          </Alert>
        ) : widgets.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(auto-fit, minmax(280px, 1fr))'
              }
            }}
          >
            {widgets.map((widget) => (
              <DashboardWidgetCard
                key={widget.dashboardWidgetId}
                widget={widget}
              />
            ))}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}
          >
            <Typography color="text.secondary">
              No widgets are configured for this dashboard.
            </Typography>
          </Paper>
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <Stack
          alignItems={{ xs: 'stretch', md: 'center' }}
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography component="h2" fontSize={18} fontWeight={700}>
            Trends
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="dashboard-date-range-label">
                Date range
              </InputLabel>
              <Select
                label="Date range"
                labelId="dashboard-date-range-label"
                onChange={(event) => setRangeSelection(event.target.value)}
                value={rangeSelection}
              >
                {DASHBOARD_DATE_RANGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {rangeSelection === DASHBOARD_DATE_RANGE.CUSTOM && (
              <>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: customToDate || today }}
                  label="From"
                  onChange={(event) => setCustomFromDate(event.target.value)}
                  size="small"
                  type="date"
                  value={customFromDate}
                />
                <TextField
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today, min: customFromDate }}
                  label="To"
                  onChange={(event) => setCustomToDate(event.target.value)}
                  size="small"
                  type="date"
                  value={customToDate}
                />
              </>
            )}
          </Stack>
        </Stack>

        {!dateRange && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Choose a valid date range. The start date must not be after the end
            date, and the range cannot exceed 366 days.
          </Alert>
        )}
        {seriesQuery.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {getDashboardErrorMessage(seriesQuery.error)}
          </Alert>
        )}
        {seriesQuery.isFetching && <LinearProgress sx={{ mb: 2 }} />}

        {visualizations.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            {visualizations.map((visualization) => (
              <DashboardVisualizationChart
                key={visualization.dashboardVisualizationId}
                seriesMetrics={seriesQuery.data?.metrics ?? []}
                visualization={visualization}
              />
            ))}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', p: 3 }}
          >
            <Typography color="text.secondary">
              No charts are configured for this dashboard.
            </Typography>
          </Paper>
        )}
      </Box>

      <DashboardArchivedMetricHistory
        dateRange={dateRange}
        locationId={locationId}
      />
    </DashboardViewerShell>
  );
};

export default DashboardViewer;
